// Прайсы и расчёт коммерческого предложения. Чистые функции, ни одного обращения в сеть.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ, А НЕ КОД ВНУТРИ ЭКРАНА
// Здесь считаются ДЕНЬГИ, которые уедут клиенту в КП. Такое обязано быть проверяемо
// тестами без отрисовки и без сети — поэтому расчёт живёт тут, а экраны
// (pages/Prices.tsx, pages/QuoteBuilder.tsx) только показывают его результат.
//
// ПОРЯДОК ДЕЙСТВИЙ В РАСЧЁТЕ ВЗЯТ У ПОРТАЛА 55, А НЕ ПРИДУМАН
// На портале (функции kpPrice/kpTotals в его worker.js) это работает так:
//   цена строки = ручная цена, если введена; иначе цена прайса × (1 + наценка/100);
//   «без НДС» пересчитывает цену делением на (1 + ставка/100), потому что прайс СиР —
//   с НДС; при «с НДС» НДС ВЫДЕЛЯЕТСЯ из суммы, при «без НДС» — НАЧИСЛЯЕТСЯ сверху.
// Мы повторяем это буквально, иначе КП из CRM и КП с портала по одним позициям дали бы
// разные суммы, и никто не смог бы сказать, какая верна.
// ЧТО ДОБАВЛЕНО СВЕРХ ПОРТАЛА: скидка. На портале её нет вовсе (там только наценка),
// а в задаче она нужна. Скидка применяется К СУММЕ, а не к строке — так её и обсуждают
// с клиентом («минус 7% на всё предложение»), и так она не размазывается по округлениям
// сотни строк.

import { fmtMoneyField } from './money'

// ─────────── адреса разделов ───────────
// Ровно эти пути нужно добавить маршрутами в App.tsx: своих маршрутов раздел себе
// прописать не может (App.tsx и components/nav.ts правят владельцы каркаса).
// В меню (components/nav.ts) сейчас стоят ВНЕШНИЕ ссылки на портал 55 — «Прайсы» и
// «Конструктор КП». Когда маршруты появятся, их надо заменить на пункты kind: 'crm'.
export const ПУТИ = {
  прайсы: '/prices',
  конструкторКП: '/quote',
} as const

export const РАЗМЕР_СТРАНИЦЫ = 50   // как PAGE в prices_api.py

// ─────────── деньги ───────────

/**
 * Цена с валютой человеку. Формат — из lib/money.ts (fmtMoneyField), своего форматирования
 * здесь нет намеренно: «1 200 000 ₽» должно выглядеть одинаково во всей CRM.
 * null → «—», потому что «цены нет» и «цена ноль» — разные утверждения.
 */
export const цена = (v: number | null | undefined, валюта = 'RUB'): string =>
  fmtMoneyField(v ?? null, валюта)

/** Округление до копеек. Деньги нельзя таскать с хвостом 0.30000000000000004. */
export const копейки = (v: number): number => Math.round(v * 100) / 100

// ─────────── годность позиции для КП ───────────

/**
 * Можно ли считать КП по цене из прайса.
 *
 * ЭТО ПРАВИЛО ОБЯЗАНО СОВПАДАТЬ с годна_для_кп() в import_prices.py и с выражением ГОДНА
 * в prices_api.py. Расхождение означало бы, что ядро обещает «посчитать можно», а экран
 * отказывается — или наоборот, что дороже: экран посчитает то, что считать нельзя.
 * Здесь оно повторено, чтобы ручные строки и позиции из старого КП тоже проверялись
 * одинаково, без похода в ядро.
 *
 * Три условия, и ни одно не придирка:
 *   • цена есть и положительна — иначе счёт не выставить (47 позиций из 687 на 28.07.2026);
 *   • цена в РУБЛЯХ — 14 позиций хранятся в CNY/USD, портал пересчитывает их по курсу ЦБ
 *     при каждом показе, ядро курса не знает. Подставить «рубли по курсу неизвестного дня»
 *     в КП — прямой убыток;
 *   • строка похожа на товар — 2 строки в прайсе это примечание («Прайс действителен
 *     с 10 февраля 2026 года» с ценой 1694 ₽), и в КП им не место.
 */
export function годнаДляКП(п: {
  price_user: number | null
  cur_user: string
  problems: string[]
}): boolean {
  return п.price_user !== null && п.price_user > 0
    && п.cur_user === 'RUB'
    && !п.problems.includes('похоже_на_примечание')
}

/** Почему позицию нельзя взять в КП. Пусто — можно. Текст идёт человеку в строку. */
export function почемуНеГодна(п: {
  price_user: number | null
  cur_user: string
  problems: string[]
}): string {
  if (п.problems.includes('похоже_на_примечание')) return 'это примечание прайса, а не товар'
  if (п.price_user === null) return 'в прайсе нет розничной цены'
  if (п.price_user <= 0) return 'розничная цена не положительна'
  if (п.cur_user !== 'RUB') return `цена в ${п.cur_user} — пересчитайте в рубли и введите вручную`
  return ''
}

/**
 * Пометки, из-за которых цену стоит перепроверить глазами, даже если считать по ней можно.
 * Это НЕ запрет: продавец должен увидеть предупреждение и решить сам.
 */
export const ПОМЕТКИ_ВНИМАНИЕ = [
  'дилер_дороже_ррц', 'вход_дороже_дилера', 'наценки_нет',
  'цена_разная_у_одного_артикула', 'подозрительно_малая_цена', 'цена_отрицательная',
] as const

export const естьВнимание = (пометки: string[]): boolean =>
  пометки.some((п) => (ПОМЕТКИ_ВНИМАНИЕ as readonly string[]).includes(п))

// ─────────── строка и условия КП ───────────

export interface СтрокаКП {
  /** src_key позиции прайса либо локальный «руч-1» для позиции, введённой руками. */
  key: string
  name: string
  pn: string
  /** Цена из прайса. null — цену из прайса брать нельзя (см. годнаДляКП). */
  base: number | null
  /** Валюта цены прайса. Нужна, чтобы объяснить, почему base не взят в расчёт. */
  cur: string
  /** Цена, введённая руками. Перебивает base — как «правка цены в строке» на портале. */
  manual: number | null
  qty: number
  unit: string
  /** Пометки прайса. Едут вместе со строкой: в КП их видно рядом с ценой. */
  problems: string[]
  /** Позиция добавлена руками, а не найдена в прайсе. */
  сама?: boolean
}

export type РежимНДС = 'gross' | 'net'

export interface УсловияКП {
  /** Наценка к цене прайса, %. Как kpMk на портале: одна на всё предложение. */
  markupPct: number
  /** Скидка от суммы, %. Сверх портала — там скидки нет. */
  discountPct: number
  /** 'gross' — цены указаны с НДС (налог выделяется), 'net' — без НДС (начисляется сверху). */
  vatMode: РежимНДС
  /** Ставка НДС, %. 0 — не облагается (УСН). */
  vatRate: number
}

export const УСЛОВИЯ_ПО_УМОЛЧАНИЮ: УсловияКП = {
  markupPct: 0,
  discountPct: 0,
  // Прайс СиР — с НДС, и портал по умолчанию тоже стоит на «с НДС» со ставкой 22%.
  vatMode: 'gross',
  vatRate: 22,
}

/** Ставки НДС. 20% оставлена для договоров, заключённых до перехода на 22%. */
export const СТАВКИ_НДС = [22, 20, 5, 0] as const

/**
 * Цена одной строки после наценки. null — цены нет вовсе (ни ручной, ни годной из прайса),
 * и такую строку в сумму брать НЕЛЬЗЯ: молча подставить ноль значило бы занизить КП.
 *
 * Наценка на РУЧНУЮ цену не применяется намеренно: человек вписал цену, которую хочет
 * видеть в предложении, а не «основу для наценки». На портале то же самое.
 */
export function ценаСтроки(с: СтрокаКП, у: УсловияКП): number | null {
  if (с.manual !== null && Number.isFinite(с.manual)) return копейки(с.manual)
  const годна = с.base !== null && с.base > 0 && с.cur === 'RUB'
    && !с.problems.includes('похоже_на_примечание')
  if (!годна) return null
  let p = (с.base as number) * (1 + у.markupPct / 100)
  // «Цены без НДС» при прайсе с НДС — это ДЕЛЕНИЕ, а не вычитание процента.
  if (у.vatMode === 'net' && у.vatRate > 0) p = p / (1 + у.vatRate / 100)
  return копейки(p)
}

export interface ИтогиКП {
  /** Строк-позиций (без строк без цены). */
  позиций: number
  /** Строк, у которых цены нет — их надо заполнить руками, иначе КП неполное. */
  безЦены: number
  /** Сумма до скидки. */
  сумма: number
  скидка: number
  послеСкидки: number
  /** НДС: выделенный из суммы (gross) или начисленный сверху (net). */
  ндс: number
  итог: number
  ставка: number
  режим: РежимНДС
}

/**
 * Итоги КП. Порядок операций: цена строки → сумма → СКИДКА → НДС.
 *
 * Скидка до НДС, а не после, — иначе при «цены с НДС» налог считался бы от суммы,
 * которую клиент не платит, и в счёте не сошёлся бы с базой.
 * При «с НДС» итог равен сумме после скидки, а НДС из неё ВЫДЕЛЕН (sub − sub/(1+r)):
 * это ровно та арифметика, что у портала, и она соответствует прайсу СиР, где цены с НДС.
 */
export function итогиКП(строки: СтрокаКП[], у: УсловияКП): ИтогиКП {
  let сумма = 0
  let позиций = 0
  let безЦены = 0
  for (const с of строки) {
    const p = ценаСтроки(с, у)
    if (p === null) {
      безЦены += 1
      continue
    }
    позиций += 1
    сумма += p * Math.max(0, с.qty)
  }
  сумма = копейки(сумма)
  const скидка = копейки(сумма * Math.min(100, Math.max(0, у.discountPct)) / 100)
  const послеСкидки = копейки(сумма - скидка)

  let ндс = 0
  let итог = послеСкидки
  if (у.vatRate > 0) {
    if (у.vatMode === 'gross') {
      ндс = копейки(послеСкидки - послеСкидки / (1 + у.vatRate / 100))
      итог = послеСкидки
    } else {
      ндс = копейки(послеСкидки * у.vatRate / 100)
      итог = копейки(послеСкидки + ндс)
    }
  }
  return { позиций, безЦены, сумма, скидка, послеСкидки, ндс, итог, ставка: у.vatRate, режим: у.vatMode }
}

/** Строка про НДС для текста КП. Ровно те же слова, что на портале. */
export function подписьНДС(у: УсловияКП): string {
  if (у.vatRate === 0) return 'НДС не облагается (УСН).'
  return у.vatMode === 'gross'
    ? `Цены указаны с НДС ${у.vatRate}%.`
    : `Цены указаны без НДС; НДС ${у.vatRate}% начисляется дополнительно.`
}

// ─────────── позиция прайса → строка КП ───────────

/**
 * Из позиции прайса делаем строку КП. Негодную цену НЕ подставляем: base остаётся,
 * но ценаСтроки() его не возьмёт, и строка честно попадёт в «без цены», пока человек
 * не впишет рубли руками. Это и есть отказ считать то, что считать нельзя.
 */
export function вСтрокуКП(п: {
  src_key: string
  name: string
  pn: string | null
  price_user: number | null
  cur_user: string
  problems: string[]
}, qty = 1): СтрокаКП {
  return {
    key: п.src_key,
    name: п.name,
    pn: п.pn || '',
    base: п.price_user,
    cur: п.cur_user,
    manual: null,
    qty: Math.max(1, qty),
    unit: 'шт',
    problems: п.problems,
  }
}

// ─────────── текст КП для копирования ───────────
// СОХРАНЕНИЯ в разделе нет: новые разделы CRM выходят сначала только на чтение, а
// сохранённое КП — это запись. Печать бланка с 28.07.2026 ЕСТЬ (lib/print1c.ts,
// `кпИзКонструктора` и `листКП`): она собирается в браузере, никуда не отправляется и
// записью не является. Плоский текст остаётся рядом — его копируют в тело письма, когда
// вложение не нужно.

export interface ШапкаКП {
  клиент: string
  номер: string
  менеджер: string
  почта: string
  условияОплаты: string
  условияПоставки: string
  прочее: string
}

export const ШАПКА_ПУСТАЯ: ШапкаКП = {
  клиент: '', номер: '', менеджер: '', почта: '',
  условияОплаты: '', условияПоставки: '', прочее: '',
}

/** Условия оплаты и поставки «по умолчанию» — скопированы из портала 55 (KP_COND_DEFAULTS). */
export const УСЛОВИЯ_ТЕКСТОМ = {
  оплата: '100% предоплата в рублях по курсу ЦБ РФ на день оплаты',
  поставка: 'до 5 рабочих дней с момента оплаты',
} as const

const строкаДенег = (n: number) => цена(n, 'RUB')

/**
 * КП плоским текстом. Формат нарочно простой: он идёт в тело письма, а не в бланк.
 * Строки без цены попадают в текст с пометкой «цена не указана» — молча выбросить их
 * значило бы отправить клиенту предложение без половины позиций.
 */
export function текстКП(строки: СтрокаКП[], у: УсловияКП, ш: ШапкаКП): string {
  const и = итогиКП(строки, у)
  const части: string[] = []
  части.push(`Коммерческое предложение${ш.номер ? ` № ${ш.номер}` : ''}`)
  if (ш.клиент) части.push(`Клиент: ${ш.клиент}`)
  части.push('')
  строки.forEach((с, n) => {
    const p = ценаСтроки(с, у)
    const хвост = p === null
      ? `цена не указана (${почемуНеГодна({ price_user: с.base, cur_user: с.cur, problems: с.problems }) || 'нужна ручная цена'})`
      : `${строкаДенег(p)} × ${с.qty} ${с.unit} = ${строкаДенег(копейки(p * с.qty))}`
    части.push(`${n + 1}. ${с.name}${с.pn ? ` [${с.pn}]` : ''} — ${хвост}`)
  })
  части.push('')
  части.push(`Сумма: ${строкаДенег(и.сумма)}`)
  if (и.скидка > 0) {
    части.push(`Скидка ${у.discountPct}%: −${строкаДенег(и.скидка)}`)
    части.push(`Сумма со скидкой: ${строкаДенег(и.послеСкидки)}`)
  }
  if (у.vatRate > 0) {
    части.push(у.vatMode === 'gross'
      ? `В том числе НДС ${у.vatRate}%: ${строкаДенег(и.ндс)}`
      : `НДС ${у.vatRate}%: ${строкаДенег(и.ндс)}`)
  }
  части.push(`ИТОГО: ${строкаДенег(и.итог)}`)
  части.push(подписьНДС(у))
  if (и.безЦены > 0) {
    части.push(`ВНИМАНИЕ: у ${и.безЦены} позиц. цена не указана и в итог НЕ включена.`)
  }
  части.push('')
  if (ш.условияОплаты) части.push(`Условия оплаты: ${ш.условияОплаты}`)
  if (ш.условияПоставки) части.push(`Условия поставки: ${ш.условияПоставки}`)
  if (ш.прочее) части.push(ш.прочее)
  if (ш.менеджер || ш.почта) {
    части.push('')
    части.push([ш.менеджер, ш.почта].filter(Boolean).join(', '))
  }
  return части.join('\n')
}
